import type { AssistantToolResult } from '@rammetec/app-kit/assistant'
import type { SelectableColumn } from '@rammetec/app-kit/query'
import {
  ASSISTANT_FIELD_SUGGESTION_CAP,
  matchesFieldBusca,
} from './assistantFilterInput'

export function isOwnField(col: SelectableColumn): boolean {
  return !col.path.includes('.')
}

function formatEnumValues(col: SelectableColumn): string {
  if (!col.enumValues?.length) return ''
  const labels = col.enumLabels
  const parts = col.enumValues.map((v) => {
    const label = labels?.[v]
    return label && label !== v ? `${v}=${label}` : v
  })
  return ` enum:[${parts.join(', ')}]`
}

/** Optional flags for `data.describeFields` (request 0295). */
export interface DescribeFieldMeta {
  /** FieldDescriptor.required — model must fill before create. */
  required?: boolean
  /** autoNumber / audit / lifecycle / id / version — do not ask the user. */
  serviceFilled?: boolean
}

/** One field line as returned by `grid.describeFields` / `data.describeFields`. */
export function describeFieldLine(
  col: SelectableColumn,
  meta?: DescribeFieldMeta,
): string {
  const flags: string[] = []
  if (meta?.serviceFilled) flags.push('preenchido pelo serviço')
  else if (meta?.required) flags.push('obrigatório')
  const flagPart = flags.length > 0 ? `, ${flags.join(', ')}` : ''
  return `${col.path} (${col.pathLabel || col.label}, ${col.type}${flagPart}${formatEnumValues(col)})`
}

/**
 * Multi-key relevance before the cap cut. Does not change who matches.
 * 1) exact path/label  2) prefix  3) own field (no `.`)  4) catalog order
 */
function compareDescribeFieldsRelevance(
  a: SelectableColumn,
  b: SelectableColumn,
  q: string,
): number {
  const score = (col: SelectableColumn) => {
    const path = col.path.toLowerCase()
    const label = col.label.toLowerCase()
    const pathLabel = col.pathLabel.toLowerCase()
    return {
      exact: path === q || label === q || pathLabel === q ? 1 : 0,
      prefix:
        path.startsWith(q) || label.startsWith(q) || pathLabel.startsWith(q) ? 1 : 0,
      own: isOwnField(col) ? 1 : 0,
    }
  }
  const sa = score(a)
  const sb = score(b)
  if (sb.exact !== sa.exact) return sb.exact - sa.exact
  if (sb.prefix !== sa.prefix) return sb.prefix - sa.prefix
  if (sb.own !== sa.own) return sb.own - sa.own
  return 0
}

export function rankDescribeFields(
  columns: readonly SelectableColumn[],
  busca: string,
): SelectableColumn[] {
  const q = busca.trim().toLowerCase()
  if (!q) return [...columns]
  return [...columns].sort((a, b) => compareDescribeFieldsRelevance(a, b, q))
}

export interface DescribeFieldsRunOptions {
  /**
   * When true and `busca` is empty, only own fields (no `.` in path) are returned,
   * with a note about how many relation fields exist — grid behaviour.
   * When false (data tools, already depth-0), all matched columns are candidates.
   * Relation tips in the message only appear when the column list actually has
   * dotted paths (`relationCount > 0`); depth-0 catalogs never invite a relation search.
   */
  preferOwnWhenEmpty?: boolean
  /** Per-path flags (obrigatório / preenchido pelo serviço) — data.describeFields. */
  fieldMeta?: ReadonlyMap<string, DescribeFieldMeta>
}

/**
 * Shared body of `grid.describeFields` and `data.describeFields` (request 0293).
 * Same cap, ranking, line format, and R6 `data` counts.
 */
export function runDescribeFields(
  filterableColumns: readonly SelectableColumn[],
  busca: string,
  options?: DescribeFieldsRunOptions,
): AssistantToolResult {
  const preferOwnWhenEmpty = options?.preferOwnWhenEmpty ?? true
  const fieldMeta = options?.fieldMeta
  const q = busca.trim()
  const matched = filterableColumns.filter((c) => matchesFieldBusca(c, busca))
  const relationCount = filterableColumns.filter((c) => !isOwnField(c)).length
  const candidates =
    !q && preferOwnWhenEmpty
      ? matched.filter(isOwnField)
      : !q
        ? matched
        : rankDescribeFields(matched, q)
  const truncated = candidates.length > ASSISTANT_FIELD_SUGGESTION_CAP
  const slice = candidates.slice(0, ASSISTANT_FIELD_SUGGESTION_CAP)

  if (slice.length === 0) {
    if (!q && preferOwnWhenEmpty && relationCount > 0) {
      return {
        ok: true,
        message: `Nenhum campo próprio filtrável. Há ${relationCount} campos de relação — busque pelo nome da relação (ex.: marca) para vê-los.`,
        data: { total: 0, returned: 0, truncated: false },
      }
    }
    const hint = q
      ? `Nenhum campo filtrável casa com "${q}".`
      : 'Nenhum campo filtrável disponível.'
    return { ok: true, message: hint }
  }

  const lines = slice
    .map((col) => describeFieldLine(col, fieldMeta?.get(col.path)))
    .join('; ')
  const notes: string[] = []
  if (!q && preferOwnWhenEmpty && relationCount > 0) {
    notes.push(
      `próprios; há ${relationCount} campos de relação — busque pelo nome da relação (ex.: marca) para vê-los`,
    )
  }
  if (truncated) {
    const truncParts = [`mostrando ${slice.length} de ${candidates.length}`]
    if (relationCount > 0) {
      truncParts.push(
        `a busca casa caminho e rótulo — busque pelo nome da relação (ex.: marca) para ver os campos dela`,
      )
    } else if (q) {
      truncParts.push('a busca casa caminho e rótulo')
    }
    notes.push(truncParts.join('; '))
  }
  const notePart = notes.length > 0 ? ` (${notes.join('; ')})` : ''

  // With `busca`, say it is a slice of the catalog — `total: matched` alone looked like
  // the entity only had that many filterable fields and triggered a second unfiltered call.
  if (q) {
    const matchedCount = candidates.length
    const catalogTotal = filterableColumns.length
    return {
      ok: true,
      message: `${matchedCount} de ${catalogTotal} campos (busca: ${q})${notePart}: ${lines}.`,
      data: {
        matched: matchedCount,
        total: catalogTotal,
        returned: slice.length,
        truncated,
      },
    }
  }

  return {
    ok: true,
    message: `Campos filtráveis${notePart}: ${lines}.`,
    data: {
      total: candidates.length,
      returned: slice.length,
      truncated,
    },
  }
}
